/**
 * Welcome Content Editor - Admin page to edit all welcome page content including Testimonials
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Save, ArrowLeft, Image, Type, Eye, Plus, Trash2, Star, RefreshCw, User, Upload, Loader2 } from 'lucide-react';
import api from '@/lib/api';

const WelcomeContentEditor = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const heroImageInputRef = useRef(null);
  const [testimonials, setTestimonials] = useState([]);
  const [showAddTestimonial, setShowAddTestimonial] = useState(false);
  const [newTestimonial, setNewTestimonial] = useState({
    name: '', role: '', location: '', image: '', rating: 5, text: '', highlight: ''
  });
  const [content, setContent] = useState({
    hero: {
      badge_text: 'Shop • Sell • Ship',
      headline: 'LIANTIAO',
      tagline: "Jamaica's Marketplace & Delivery",
      description: 'Shop great deals, sell your products, and we deliver! Everyday specials, low prices, fast island-wide delivery.',
      hero_image: '',
      stat1_value: 'LOW', stat1_label: 'Prices',
      stat2_value: 'DAILY', stat2_label: 'Specials',
      stat3_value: 'GREAT', stat3_label: 'Deals',
    },
    featured_stores: {
      section_badge: 'Featured Stores Near You',
      section_title: 'SHOP FROM LOCAL SELLERS',
      section_highlight: 'LOCAL SELLERS',
    },
    shop: {
      section_badge: 'Great Deals Every Day',
      section_title: "TODAY'S HOT DEALS",
      section_highlight: 'HOT DEALS',
      section_description: 'Shop from local Jamaican sellers and get the best prices. New specials added daily!',
      promo_title: 'FREE DELIVERY ON FIRST ORDER!',
      promo_description: 'Sign up today and enjoy free delivery on your first purchase.',
    },
    sell: {
      section_badge: 'For Businesses & Individuals',
      section_title: 'SELL ON LIANTIAO',
      section_highlight: 'LIANTIAO',
      section_description: "Join hundreds of Jamaican sellers. List your products, we deliver to your customers. It's that simple.",
      cta_title: 'START SELLING TODAY',
      cta_subtitle: 'No signup fees, no monthly charges',
    },
    services: {
      section_badge: 'Why Liantiao',
      section_title: 'SHOP, SELL & DELIVER',
      section_highlight: 'DELIVER',
      section_description: "Jamaica's marketplace connecting buyers and sellers. Great deals every day, with delivery handled by us.",
    },
    coverage: {
      section_badge: 'Delivery Coverage',
      section_title: 'NOW SERVING 6 PARISHES',
      section_highlight: '6 PARISHES',
      section_description: 'Shop, sell, and get deliveries across Kingston, St. Thomas, St. Catherine, Clarendon, St. Ann & Falmouth. More parishes coming soon!',
      map_image: '',
    },
    pudo: {
      section_badge: 'PUDO Network',
      section_title: 'FIND A PUDO CENTRE',
      section_highlight: 'PUDO CENTRE',
      section_description: 'Over 120 pickup and drop-off locations across our 6 active parishes. Convenient, secure, and always nearby.',
    },
    testimonials: {
      section_badge: 'Customer Stories',
      section_title: 'TRUSTED BY JAMAICANS',
      section_highlight: 'JAMAICANS',
      section_description: "Don't just take our word for it. Here's what our customers across Jamaica have to say about Liantiao.",
    },
    cta: {
      section_badge: 'Download the App',
      section_title: 'SHOP & SELL ON LIANTIAO',
      section_highlight: 'LIANTIAO',
      section_description: 'Great deals, everyday specials, low prices. Shop from local sellers or start selling your own products. Download the app today!',
      background_image: '',
    },
    footer: {
      company_description: "Jamaica's marketplace for great deals and local products. Shop, sell, and get fast delivery across Kingston, St. Thomas, St. Catherine, Clarendon, St. Ann & Falmouth. More parishes coming soon!",
      phone: '+1 876-555-LIAN (5426)',
      email: 'hello@liantiaoja.com',
      support_hours: '24/7 Customer Support',
    },
    join_us: {
      section_badge: 'Join the Liantiao Family',
      section_title: 'PARTNER WITH LIANTIAO',
      section_highlight: 'LIANTIAO',
      section_description: 'Built by Jamaicans, Powering Jamaica. Join our growing network of drivers, PUDO centres, and businesses.',
      ppv_title: 'Are you a PPV Driver?',
      ppv_description: 'Got a vehicle? Turn your trunk into income. Deliver packages on YOUR schedule.',
      pudo_title: 'BECOME A PUDO CENTRE',
      pudo_description: 'Have a shop or business? Earn extra by becoming a pickup & drop-off point.',
      business_title: 'SELL YOUR PRODUCTS',
      business_description: 'Business or individual? List your products and let us handle delivery island-wide.',
    },
  });

  useEffect(() => {
    if (!user || !['admin', 'super_admin', 'ADMIN', 'CEO'].includes(user.role)) {
      navigate('/login');
      return;
    }
    fetchContent();
    fetchTestimonials();
  }, [user, navigate]);

  const fetchContent = async () => {
    try {
      const response = await api.get('/admin/welcome-content');
      if (response.data) {
        setContent(prev => ({
          ...prev,
          ...Object.keys(response.data).reduce((acc, key) => {
            if (response.data[key]) acc[key] = { ...prev[key], ...response.data[key] };
            return acc;
          }, {})
        }));
      }
    } catch (error) {
      console.log('Using default content');
    }
    setLoading(false);
  };

  const fetchTestimonials = async () => {
    try {
      const response = await api.get('/admin/welcome-content/testimonials');
      if (response.data) setTestimonials(response.data);
    } catch (error) {
      console.log('No testimonials found');
    }
  };

  const handleAddTestimonial = async () => {
    if (!newTestimonial.name || !newTestimonial.text) {
      toast.error('Name and testimonial text are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/welcome-content/testimonials', newTestimonial);
      toast.success('Testimonial added!');
      setNewTestimonial({ name: '', role: '', location: '', image: '', rating: 5, text: '', highlight: '' });
      setShowAddTestimonial(false);
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to add testimonial');
    }
    setSaving(false);
  };

  const handleDeleteTestimonial = async (id) => {
    if (!confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      await api.delete(`/admin/welcome-content/testimonials/${id}`);
      toast.success('Testimonial deleted');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  const handleChange = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  // Handle image file upload
  const handleImageUpload = async (section, field, file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'welcome_content');
      const response = await api.post('/uploads/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data?.url) {
        handleChange(section, field, response.data.url);
        toast.success('Image uploaded successfully!');
      }
    } catch (error) {
      toast.error('Failed to upload image: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/welcome-content', content);
      toast.success('Welcome content saved successfully!');
    } catch (error) {
      toast.error('Failed to save content');
    }
    setSaving(false);
  };

  const renderTextField = (section, field, label, multiline = false) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      {multiline ? (
        <Textarea value={content[section]?.[field] || ''} onChange={(e) => handleChange(section, field, e.target.value)} className="min-h-[100px]" />
      ) : (
        <Input value={content[section]?.[field] || ''} onChange={(e) => handleChange(section, field, e.target.value)} />
      )}
    </div>
  );

  const renderImageField = (section, field, label) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium flex items-center gap-2"><Image className="w-4 h-4" />{label}</Label>
      <div className="flex gap-2">
        <Input 
          value={content[section]?.[field] || ''} 
          onChange={(e) => handleChange(section, field, e.target.value)} 
          placeholder="Enter image URL or upload..." 
          className="flex-1"
        />
        <input
          type="file"
          accept="image/*"
          className="hidden"
          id={`upload-${section}-${field}`}
          onChange={(e) => handleImageUpload(section, field, e.target.files?.[0])}
        />
        <Button 
          type="button"
          variant="outline"
          disabled={uploadingImage}
          onClick={() => document.getElementById(`upload-${section}-${field}`).click()}
          className="shrink-0"
        >
          {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          <span className="ml-2 hidden sm:inline">Upload</span>
        </Button>
      </div>
      {content[section]?.[field] && (
        <div className="mt-2 rounded-lg overflow-hidden border border-border h-32">
          <img src={content[section][field]} alt="Preview" className="w-full h-full object-cover" />
        </div>
      )}
    </div>
  );

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="bg-card border-b border-border sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}><ArrowLeft className="w-5 h-5" /></Button>
              <div><h1 className="text-xl font-bold text-foreground">Welcome Page Editor</h1><p className="text-sm text-muted-foreground">Edit all text and images on the welcome page</p></div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => window.open('/', '_blank')}><Eye className="w-4 h-4 mr-2" />Preview</Button>
              <Button variant="jamaica" onClick={handleSave} disabled={saving}><Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="hero" className="space-y-6">
          <TabsList className="grid grid-cols-4 lg:grid-cols-11 gap-1 h-auto p-1 bg-card">
            <TabsTrigger value="hero" className="text-xs">Hero</TabsTrigger>
            <TabsTrigger value="featured" className="text-xs">Stores</TabsTrigger>
            <TabsTrigger value="shop" className="text-xs">Shop</TabsTrigger>
            <TabsTrigger value="sell" className="text-xs">Sell</TabsTrigger>
            <TabsTrigger value="services" className="text-xs">Services</TabsTrigger>
            <TabsTrigger value="coverage" className="text-xs">Coverage</TabsTrigger>
            <TabsTrigger value="pudo" className="text-xs">PUDO</TabsTrigger>
            <TabsTrigger value="testimonials" className="text-xs">Reviews</TabsTrigger>
            <TabsTrigger value="cta" className="text-xs">CTA</TabsTrigger>
            <TabsTrigger value="footer" className="text-xs">Footer</TabsTrigger>
            <TabsTrigger value="joinus" className="text-xs">Join Us</TabsTrigger>
          </TabsList>

          <TabsContent value="hero">
            <Card><CardHeader><CardTitle className="flex items-center gap-2"><Type className="w-5 h-5" />Hero Section</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                {renderImageField('hero', 'hero_image', 'Background Image URL')}
                <div className="grid md:grid-cols-2 gap-4">{renderTextField('hero', 'badge_text', 'Badge Text')}{renderTextField('hero', 'headline', 'Main Headline')}{renderTextField('hero', 'tagline', 'Tagline')}</div>
                {renderTextField('hero', 'description', 'Description', true)}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{renderTextField('hero', 'stat1_value', 'Stat 1 Value')}{renderTextField('hero', 'stat1_label', 'Stat 1 Label')}{renderTextField('hero', 'stat2_value', 'Stat 2 Value')}{renderTextField('hero', 'stat2_label', 'Stat 2 Label')}{renderTextField('hero', 'stat3_value', 'Stat 3 Value')}{renderTextField('hero', 'stat3_label', 'Stat 3 Label')}</div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="featured"><Card><CardHeader><CardTitle>Featured Stores Section</CardTitle></CardHeader><CardContent className="space-y-4">{renderTextField('featured_stores', 'section_badge', 'Section Badge')}{renderTextField('featured_stores', 'section_title', 'Section Title')}{renderTextField('featured_stores', 'section_highlight', 'Highlighted Text')}</CardContent></Card></TabsContent>

          <TabsContent value="shop"><Card><CardHeader><CardTitle>Shop Section</CardTitle></CardHeader><CardContent className="space-y-4">{renderTextField('shop', 'section_badge', 'Section Badge')}{renderTextField('shop', 'section_title', 'Section Title')}{renderTextField('shop', 'section_highlight', 'Highlighted Text')}{renderTextField('shop', 'section_description', 'Description', true)}{renderTextField('shop', 'promo_title', 'Promo Banner Title')}{renderTextField('shop', 'promo_description', 'Promo Banner Description', true)}</CardContent></Card></TabsContent>

          <TabsContent value="sell"><Card><CardHeader><CardTitle>Sell Section</CardTitle></CardHeader><CardContent className="space-y-4">{renderTextField('sell', 'section_badge', 'Section Badge')}{renderTextField('sell', 'section_title', 'Section Title')}{renderTextField('sell', 'section_highlight', 'Highlighted Text')}{renderTextField('sell', 'section_description', 'Description', true)}{renderTextField('sell', 'cta_title', 'CTA Title')}{renderTextField('sell', 'cta_subtitle', 'CTA Subtitle')}</CardContent></Card></TabsContent>

          <TabsContent value="services"><Card><CardHeader><CardTitle>Services Section</CardTitle></CardHeader><CardContent className="space-y-4">{renderTextField('services', 'section_badge', 'Section Badge')}{renderTextField('services', 'section_title', 'Section Title')}{renderTextField('services', 'section_highlight', 'Highlighted Text')}{renderTextField('services', 'section_description', 'Description', true)}</CardContent></Card></TabsContent>

          <TabsContent value="coverage"><Card><CardHeader><CardTitle>Coverage Section</CardTitle></CardHeader><CardContent className="space-y-4">{renderTextField('coverage', 'section_badge', 'Section Badge')}{renderTextField('coverage', 'section_title', 'Section Title')}{renderTextField('coverage', 'section_highlight', 'Highlighted Text')}{renderTextField('coverage', 'section_description', 'Description', true)}{renderImageField('coverage', 'map_image', 'Map Background Image')}</CardContent></Card></TabsContent>

          <TabsContent value="pudo"><Card><CardHeader><CardTitle>PUDO Section</CardTitle></CardHeader><CardContent className="space-y-4">{renderTextField('pudo', 'section_badge', 'Section Badge')}{renderTextField('pudo', 'section_title', 'Section Title')}{renderTextField('pudo', 'section_highlight', 'Highlighted Text')}{renderTextField('pudo', 'section_description', 'Description', true)}</CardContent></Card></TabsContent>

          {/* TESTIMONIALS TAB - Enhanced with CRUD */}
          <TabsContent value="testimonials">
            <div className="space-y-6">
              <Card>
                <CardHeader><CardTitle>Testimonials Section Text</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {renderTextField('testimonials', 'section_badge', 'Section Badge')}
                  {renderTextField('testimonials', 'section_title', 'Section Title')}
                  {renderTextField('testimonials', 'section_highlight', 'Highlighted Text')}
                  {renderTextField('testimonials', 'section_description', 'Description', true)}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2"><Star className="w-5 h-5 text-secondary" />Manage Testimonials ({testimonials.length})</CardTitle>
                    <Button variant="jamaica" onClick={() => setShowAddTestimonial(true)}><Plus className="w-4 h-4 mr-2" />Add Testimonial</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Add Testimonial Form */}
                  {showAddTestimonial && (
                    <div className="mb-6 p-6 border border-primary/30 rounded-lg bg-primary/5">
                      <h4 className="font-semibold mb-4 flex items-center gap-2"><User className="w-4 h-4" />New Testimonial</h4>
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div><Label>Name *</Label><Input value={newTestimonial.name} onChange={(e) => setNewTestimonial({...newTestimonial, name: e.target.value})} placeholder="Customer name" /></div>
                        <div><Label>Role</Label><Input value={newTestimonial.role} onChange={(e) => setNewTestimonial({...newTestimonial, role: e.target.value})} placeholder="e.g., Shop Owner, Regular Shopper" /></div>
                        <div><Label>Location</Label><Input value={newTestimonial.location} onChange={(e) => setNewTestimonial({...newTestimonial, location: e.target.value})} placeholder="e.g., Kingston, St. Ann" /></div>
                        <div><Label>Photo URL</Label><Input value={newTestimonial.image} onChange={(e) => setNewTestimonial({...newTestimonial, image: e.target.value})} placeholder="https://..." /></div>
                        <div><Label>Highlight Tag</Label><Input value={newTestimonial.highlight} onChange={(e) => setNewTestimonial({...newTestimonial, highlight: e.target.value})} placeholder="e.g., Sales tripled, Best prices" /></div>
                        <div>
                          <Label>Rating (1-5)</Label>
                          <div className="flex gap-1 mt-2">
                            {[1,2,3,4,5].map(r => (
                              <button key={r} onClick={() => setNewTestimonial({...newTestimonial, rating: r})} className={`p-1 ${newTestimonial.rating >= r ? 'text-secondary' : 'text-muted-foreground'}`}>
                                <Star className={`w-6 h-6 ${newTestimonial.rating >= r ? 'fill-secondary' : ''}`} />
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="mb-4"><Label>Testimonial Text *</Label><Textarea value={newTestimonial.text} onChange={(e) => setNewTestimonial({...newTestimonial, text: e.target.value})} placeholder="What the customer said about Liantiao..." className="min-h-[100px]" /></div>
                      <div className="flex gap-3">
                        <Button variant="outline" onClick={() => setShowAddTestimonial(false)}>Cancel</Button>
                        <Button variant="jamaica" onClick={handleAddTestimonial} disabled={saving}>{saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}Add Testimonial</Button>
                      </div>
                    </div>
                  )}

                  {/* Testimonials List */}
                  <div className="space-y-4">
                    {testimonials.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Star className="w-12 h-12 mx-auto mb-4 opacity-30" />
                        <p>No testimonials yet. Add your first customer story!</p>
                      </div>
                    ) : (
                      testimonials.map((t, index) => (
                        <div key={t._id || index} className="p-4 border border-border rounded-lg hover:border-primary/30 transition-colors">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 rounded-full bg-muted overflow-hidden shrink-0">
                              {t.image ? <img src={t.image} alt={t.name} className="w-full h-full object-cover" /> : <User className="w-full h-full p-3 text-muted-foreground" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-4">
                                <div>
                                  <h4 className="font-semibold text-foreground">{t.name}</h4>
                                  <p className="text-sm text-muted-foreground">{t.role} • {t.location}</p>
                                  <div className="flex gap-0.5 mt-1">
                                    {[...Array(t.rating || 5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-secondary text-secondary" />)}
                                  </div>
                                </div>
                                <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => handleDeleteTestimonial(t._id)}><Trash2 className="w-4 h-4" /></Button>
                              </div>
                              {t.highlight && <span className="inline-block mt-2 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded">{t.highlight}</span>}
                              <p className="mt-2 text-foreground/80 text-sm italic">"{t.text}"</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="cta"><Card><CardHeader><CardTitle>CTA Section</CardTitle></CardHeader><CardContent className="space-y-4">{renderImageField('cta', 'background_image', 'Background Image URL')}{renderTextField('cta', 'section_badge', 'Section Badge')}{renderTextField('cta', 'section_title', 'Section Title')}{renderTextField('cta', 'section_highlight', 'Highlighted Text')}{renderTextField('cta', 'section_description', 'Description', true)}</CardContent></Card></TabsContent>

          <TabsContent value="footer"><Card><CardHeader><CardTitle>Footer Section</CardTitle></CardHeader><CardContent className="space-y-4">{renderTextField('footer', 'company_description', 'Company Description', true)}{renderTextField('footer', 'phone', 'Phone Number')}{renderTextField('footer', 'email', 'Email Address')}{renderTextField('footer', 'support_hours', 'Support Hours')}</CardContent></Card></TabsContent>

          <TabsContent value="joinus"><Card><CardHeader><CardTitle>Join Us Section (Partner Recruitment)</CardTitle></CardHeader><CardContent className="space-y-4">{renderTextField('join_us', 'section_badge', 'Section Badge')}{renderTextField('join_us', 'section_title', 'Section Title')}{renderTextField('join_us', 'section_highlight', 'Highlighted Text')}{renderTextField('join_us', 'section_description', 'Description', true)}<div className="border-t pt-4 mt-4"><h4 className="font-medium mb-3">PPV Driver Card</h4>{renderTextField('join_us', 'ppv_title', 'PPV Title')}{renderTextField('join_us', 'ppv_description', 'PPV Description', true)}</div><div className="border-t pt-4 mt-4"><h4 className="font-medium mb-3">PUDO Centre Card</h4>{renderTextField('join_us', 'pudo_title', 'PUDO Title')}{renderTextField('join_us', 'pudo_description', 'PUDO Description', true)}</div><div className="border-t pt-4 mt-4"><h4 className="font-medium mb-3">Business Card</h4>{renderTextField('join_us', 'business_title', 'Business Title')}{renderTextField('join_us', 'business_description', 'Business Description', true)}</div></CardContent></Card></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default WelcomeContentEditor;
